// solo/RetailRecommendations.jsx
// Surfaces retail products flagged against the booking's services as
// suggestions. Two presentation modes:
//
//   - Till mode (default): MUI primitives, designed to drop into the
//     authenticated solo dashboard / appointment dialogs.
//   - Public mode (when publicSlug is set): plain divs with bp-rec-*
//     classes, designed to drop into PublicBookingPage.jsx and inherit
//     the booking page's visual language.
//
// Both modes share the same fetch, dedup, and auto-suppress behaviour.
// The component renders nothing when there are no recommendations.

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Stack,
  Paper,
} from '@mui/material';
import {
  ShoppingBag as ShoppingBagIcon,
  AddShoppingCart as AddShoppingCartIcon,
} from '@mui/icons-material';
import api from '../axiosConfig';

const formatMoney = (value) => `£${Number(value || 0).toFixed(2)}`;

const RetailRecommendations = ({
  serviceIds = [],
  onAddToSale = null,
  publicSlug = null,
}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const publicMode = !!publicSlug;

  // Stabilise the dependency — array identity changes on every parent
  // render even when contents are identical.
  const serviceIdsKey = useMemo(
    () => [...serviceIds].sort((a, b) => a - b).join(','),
    [serviceIds]
  );

  useEffect(() => {
    let cancelled = false;

    if (!serviceIdsKey) {
      setRecommendations([]);
      setLoaded(true);
      return;
    }

    setLoaded(false);

    // Public booking flow uses an unauthenticated, slug-scoped endpoint;
    // till and admin contexts use the JWT-scoped one via the configured
    // axios instance.
    const request = publicSlug
      ? fetch(
          `/api/booking/retail-recommendations/${publicSlug}` +
            `?service_ids=${serviceIdsKey}`
        ).then((r) => (r.ok ? r.json() : []))
      : api
          .get(
            `/service-retail-flags/for-services?service_ids=${serviceIdsKey}`
          )
          .then((res) => res.data);

    request
      .then((data) => {
        if (cancelled) return;
        setRecommendations(Array.isArray(data) ? data : []);
        setLoaded(true);
      })
      .catch((err) => {
        console.error('RetailRecommendations: failed to load', err);
        if (!cancelled) {
          setRecommendations([]);
          setLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [serviceIdsKey, publicSlug]);

  // Suppress entirely until we have something to show.
  if (!loaded || recommendations.length === 0) return null;

  // -- Public mode: client-facing on PublicBookingPage --------------------
  if (publicMode) {
    return (
      <div className="bp-rec">
        <div className="bp-rec-header">
          <h3 className="bp-rec-title">What to use at home</h3>
          <p className="bp-rec-sub">Suggested by your salon</p>
        </div>
        <div className="bp-rec-list">
          {recommendations.map((rec) => (
            <div key={rec.product_id} className="bp-rec-item">
              <div className="bp-rec-row">
                <span className="bp-rec-name">{rec.product_name}</span>
                <span className="bp-rec-price">
                  {formatMoney(rec.retail_price)}
                </span>
              </div>
              {rec.triggered_by?.length > 0 && (
                <div className="bp-rec-for">
                  for {rec.triggered_by.join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // -- Till mode: MUI primitives for the solo dashboard -------------------
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ShoppingBagIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
          Recommended retail
        </Typography>
        <Chip
          label={recommendations.length}
          size="small"
          sx={{ height: 20, fontSize: '11px' }}
        />
      </Box>

      <Stack spacing={1}>
        {recommendations.map((rec) => (
          <Paper
            key={rec.product_id}
            variant="outlined"
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
                <Typography variant="body2" fontWeight="500" noWrap>
                  {rec.product_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatMoney(rec.retail_price)}
                </Typography>
              </Box>

              {rec.triggered_by?.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  for {rec.triggered_by.join(', ')}
                </Typography>
              )}

              {rec.barcode && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: 'block', fontFamily: 'monospace', mt: 0.25 }}
                >
                  {rec.barcode}
                </Typography>
              )}
            </Box>

            {onAddToSale && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddShoppingCartIcon />}
                onClick={() => onAddToSale(rec)}
                sx={{ minHeight: 36 /* tablet-friendly tap target */ }}
              >
                Add to sale
              </Button>
            )}
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default RetailRecommendations;